import { CloseIcon, ImageUnavailableIcon } from '@fluentui/react-northstar';
import { ContentBlock, ContentState, EditorState, Modifier, SelectionState } from 'draft-js';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { ImageAlignment, catalogImageId, clampWidthPx, imageSizeToken, imageWidthCss, parseImageTitle, placeholderLayout } from './catalogImages';

/**
 * The editor's picture, in place of react-draft-wysiwyg's own: that one only offers L / C / R. This adds a
 * corner handle to drag the width, stored in pixels. A percentage of the editor was tried first and did not
 * survive contact with Teams: the editor is several times wider than a card, so a picture that looked bigger
 * here came out smaller there (item 104). Pixels mean the same size everywhere, up to the width of wherever
 * the picture is shown. The drag stops at the picture's own size - enlarging past it only blurs it.
 *
 * Changing entity data does not re-render the block by itself - draft-js compares blocks, and the block is
 * unchanged - so what was just set is also held locally, the way the library's own component forces it.
 */
/** What the editor passes each picture block - see `EditorComponent.renderPictureBlock`. */
export interface PictureBlockProps {
  onChange: (editorState: EditorState) => void;
  getEditorState: () => EditorState;
  isReadOnly?: () => boolean;
}

interface ResizableImageProps {
  block: ContentBlock;
  contentState: ContentState;
  blockProps: PictureBlockProps;
}

export const ResizableImage = (props: ResizableImageProps) => {
  const { block, contentState, blockProps } = props;
  const entityKey = block.getEntityAt(0);
  const data = contentState.getEntity(entityKey).getData();
  const stored = parseImageTitle(`${data.alignment ?? ''} ${data.width ?? ''}`);

  const [hovered, setHovered] = useState(false);
  const [local, setLocal] = useState<{ alignment?: ImageAlignment; width?: string }>({});
  const [dragging, setDragging] = useState<number | null>(null);
  // The widest the drag allows. Reaching it simply stops the picture growing, which read as the handle being stuck,
  // so the size label says it is the limit.
  const [limit, setLimit] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const alignment = local.alignment ?? stored.alignment ?? 'left';
  const width = dragging !== null ? `${dragging}px` : local.width ?? imageWidthCss(stored);
  const readOnly = blockProps.isReadOnly?.();
  // A stored picture arrives as its marker, which the browser cannot load, and is swapped for the fetched picture a
  // moment later (Editor.showStoredPictures); a linked one loads from the web. Until either is drawn its space
  // shimmers, as on the catalog list and the portal, so the text does not jump when it lands. A broken link counts
  // as drawn, so it shows as it did before rather than shimmering for good.
  const [drawn, setDrawn] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setDrawn(false); setFailed(false); }, [data.src]);
  const isMarker = !!catalogImageId(data.src);
  // A picture that cannot be loaded - a link to something that is not a picture, or a stored one that is gone - is
  // drawn as a box saying so, with its X. Shown as nothing, as it is to readers, it could not be found to take out.
  const broken = failed || !!data.broken;
  const loading = !broken && (isMarker || !drawn);

  const save = (changes: Record<string, string>) => {
    const editorState = blockProps.getEditorState();
    const content = editorState.getCurrentContent().mergeEntityData(entityKey, changes);
    blockProps.onChange(EditorState.push(editorState, content, 'change-block-data'));
  };

  /**
   * The X: takes the picture's block out, the same as selecting it and pressing Delete, and undo brings it back.
   * The cursor goes to the end of the line before, or the start of the line after. A picture that is the only
   * thing in the answer becomes an empty line, since an editor cannot hold no lines at all.
   */
  const remove = () => {
    const editorState = blockProps.getEditorState();
    const content = editorState.getCurrentContent();
    const key = block.getKey();
    const before = content.getBlockBefore(key);
    const after = content.getBlockAfter(key);
    let next: ContentState;
    if (!before && !after) {
      const whole = SelectionState.createEmpty(key).merge({ focusOffset: block.getLength() }) as SelectionState;
      next = Modifier.setBlockType(Modifier.removeRange(content, whole, 'backward'), whole, 'unstyled');
    } else {
      const target = before ?? after;
      const offset = before ? before.getLength() : 0;
      const cursor = SelectionState.createEmpty(target.getKey()).merge({ anchorOffset: offset, focusOffset: offset }) as SelectionState;
      next = content.merge({
        blockMap: content.getBlockMap().delete(key),
        selectionBefore: editorState.getSelection(),
        selectionAfter: cursor
      }) as ContentState;
    }
    blockProps.onChange(EditorState.forceSelection(EditorState.push(editorState, next, 'remove-range'), next.getSelectionAfter()));
  };

  /**
   * Notes the picture's own size, which is saved in its title so a page can hold the exact space while the
   * picture loads. Written straight into the entity rather than through onChange: in draft-js 0.11
   * mergeEntityData changes the shared entity store in place, so the next save carries it without this counting
   * as an edit, adding an undo step, or lighting up Save on an answer that was only opened.
   */
  const recordSize = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    const size = imageSizeToken(naturalWidth, naturalHeight);
    if (data.size !== size) contentState.mergeEntityData(entityKey, { size });
  };

  const align = (next: ImageAlignment) => {
    setLocal(prev => ({ ...prev, alignment: next }));
    save({ alignment: next === 'center' ? 'none' : next });
  };

  const startResize = (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const wrapper = wrapperRef.current;
    const editor = wrapper?.closest<HTMLElement>('.rdw-editor-main');
    if (!wrapper || !editor) return;

    const style = getComputedStyle(editor);
    const available = editor.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const natural = wrapper.querySelector('img')?.naturalWidth || 0;
    const largest = Math.min(natural || available, available);
    setLimit(largest);
    const startWidth = wrapper.getBoundingClientRect().width;
    const startX = e.clientX;
    // Dragging from a centred picture moves both edges; from a right-aligned one, the handle is on the left.
    const direction = alignment === 'right' ? -1 : 1;
    const factor = alignment === 'center' ? 2 : 1;
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);

    let px = clampWidthPx(startWidth, largest);
    setDragging(px);

    const move = (ev: PointerEvent) => {
      px = clampWidthPx(startWidth + (ev.clientX - startX) * direction * factor, largest);
      setDragging(px);
    };
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      setDragging(null);
      setLocal(prev => ({ ...prev, width: `${px}px` }));
      save({ width: `${px}px` });
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  const option = (value: ImageAlignment, label: string) => (
    <div
      title={i18next.t(`knowledge.add-knowledge-modal.align-${value}`)}
      aria-label={i18next.t(`knowledge.add-knowledge-modal.align-${value}`)}
      className={`catalog-image-align-option${alignment === value ? ' catalog-image-align-option--active' : ''}`}
      onMouseDown={e => { e.preventDefault(); align(value); }}
    >{label}</div>
  );

  return (
    <span
      className={`rdw-image-alignment rdw-image-${alignment}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span
        ref={wrapperRef}
        className="rdw-image-imagewrapper catalog-image-resizable"
        style={width ? { width } : undefined}
      >
        {broken && (
          <span className="catalog-image-broken" role="img" aria-label={i18next.t('knowledge.add-knowledge-modal.image-broken')}
            style={width ? { width: '100%' } : undefined}>
            <ImageUnavailableIcon size="large" />
            <span>{i18next.t('knowledge.add-knowledge-modal.image-broken')}</span>
          </span>
        )}
        {loading && (
          <span className="catalog-image-placeholder" role="img" aria-label={data.alt ?? ''} aria-busy="true"
            style={{ ...placeholderLayout(`${data.width ?? ''} ${data.size ?? ''}`), ...(width ? { width: '100%' } : {}) }} />
        )}
        {!isMarker && !failed && (
          <img src={data.src} alt={data.alt ?? ''} draggable={false}
            style={{ ...(width ? { width: '100%' } : {}), ...(drawn ? {} : { display: 'none' }) }}
            ref={el => { if (!drawn && el?.complete) setDrawn(true); }}
            onLoad={e => { recordSize(e); setDrawn(true); }} onError={() => setFailed(true)} />
        )}
        {!readOnly && !loading && (hovered || dragging !== null) && (
          <>
            {!broken && <span
              className={`catalog-image-resize-handle catalog-image-resize-handle--${alignment === 'right' ? 'left' : 'right'}`}
              onPointerDown={startResize}
              role="separator"
              aria-orientation="vertical"
              aria-label={i18next.t('knowledge.add-knowledge-modal.image-resize')}
            />}
            {dragging !== null
              ? <span className="catalog-image-size-label">{limit !== null && dragging >= Math.round(limit) ? i18next.t('knowledge.add-knowledge-modal.image-size-max', { width: dragging }) : `${dragging}px`}</span>
              : (
                <button type="button" className="catalog-image-remove"
                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); remove(); }}
                  title={i18next.t('knowledge.add-knowledge-modal.image-remove')} aria-label={i18next.t('knowledge.add-knowledge-modal.image-remove')}>
                  <CloseIcon size="small" />
                </button>
              )}
            <div className={`rdw-image-alignment-options-popup catalog-image-align${alignment === 'right' ? ' rdw-image-alignment-options-popup-right' : ''}`}>
              {option('left', i18next.t('knowledge.add-knowledge-modal.align-left-short'))}
              {option('center', i18next.t('knowledge.add-knowledge-modal.align-center-short'))}
              {option('right', i18next.t('knowledge.add-knowledge-modal.align-right-short'))}
            </div>
          </>
        )}
      </span>
    </span>
  );
};
